import { useCallback, useEffect, useRef, useState } from 'react';
import { getMostRecentAccountsStatusesList } from 'connections-embed-lib';
import getLang from 'I18n/utils/getLang';
import { useFetchAllForUserQuery } from '../../../__generated__/chirp/com/hubspot/connected/application/status/rpc/frontend/ConnectedStatusGraphQLService.hooks';

const POLL_INTERVAL_MS = 1000;
const POLL_DURATION_MS = 20000;

type TStatusCategory = 'AUTHORIZATION' | 'NOTIFICATION';

export function useAppStatus({
  appId,
  shouldFetch = true,
  category,
  currentUserId,
}: {
  appId?: number | string;
  shouldFetch?: boolean;
  category?: TStatusCategory;
  currentUserId: number;
}) {
  const numericAppId = typeof appId === 'string' ? parseInt(appId, 10) : appId;
  const isValidAppId =
    typeof numericAppId === 'number' &&
    !isNaN(numericAppId) &&
    numericAppId > 0;
  const isValidCategory =
    !!category && ['AUTHORIZATION', 'NOTIFICATION'].includes(category);

  const { data, loading, error, refetch } = useFetchAllForUserQuery({
    variables: {
      locale: getLang(),
      statusFetchOptions: {
        appIds: isValidAppId ? [numericAppId] : [],
        ...(isValidCategory && { categories: [category] }),
      },
    },
    skip: !shouldFetch || !isValidAppId,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const pollForStatus = useCallback(() => {
    if (intervalRef.current) {
      // Already polling, do nothing
      return;
    }

    setIsPolling(true);

    intervalRef.current = setInterval(async () => {
      const { data: refetchData } = await refetch();
      const hasOkStatus = refetchData?.result?.results?.some(
        (result) => result.status === 'Connected'
      );
      if (hasOkStatus) {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);

    timeoutRef.current = setTimeout(stopPolling, POLL_DURATION_MS);
  }, [refetch, stopPolling]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const statusesList = data?.results || [];
  /**
   * Current user should not to see statuses for another users. So, we need to show:
   * 1. Statuses without `userId`
   * 2. Statuses with `userId === currentUserId`
   */
  const filteredStatusesList = statusesList.filter(({ userId }) => {
    return !userId || userId === currentUserId;
  });
  const statusData = getMostRecentAccountsStatusesList(filteredStatusesList);
  const hasStatusData = statusData && statusData.length > 0;

  return {
    statusData: hasStatusData ? statusData : undefined,
    statusLoading: loading || isPolling,
    statusError: error,
    pollForStatus,
    refetch,
  };
}
